import React, { useState } from "react";
import { useWorks } from "../contexts/WorkContext";
import COLORS from "../constants/colors";
import { BASEURL } from "../constants/baseUrl";
import "./WorkModal.css";

const WorkModal = () => {
  const { selectedWork, selectWork } = useWorks();
  const [zoomImage, setZoomImage] = useState<string | null>(null);

  if (!selectedWork) return null;

  // =========================
  // sanitize & bold
  // =========================
  function sanitizeAndBold(htmlString: string): string {
    let html = htmlString;
    html = html.replace(/<b>/g, '<br /><br /><b>');
    html = html.replace(/<\/b>/g, '</b><br />');
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    function cleanNode(node: ChildNode): Node | null {
      if (node.nodeType === Node.TEXT_NODE) {
        const textNode = node as Text;
        textNode.textContent = textNode.textContent?.trim() || '';
        return textNode.textContent ? textNode : null;
      }
      if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as HTMLElement;
        const children = Array.from(element.childNodes)
          .map(cleanNode)
          .filter((n): n is Node => n !== null);
        if (element.tagName === 'B' && children.length === 0) return null;
        element.innerHTML = '';
        children.forEach(c => element.appendChild(c));
        return element;
      }
      return node;
    }

    const cleanedNodes = Array.from(tempDiv.childNodes)
      .map(cleanNode)
      .filter((n): n is Node => n !== null);

    const finalDiv = document.createElement('div');
    cleanedNodes.forEach(n => finalDiv.appendChild(n));

    return finalDiv.innerHTML;
  }

  // =========================
  // Médias
  // =========================
  const isImageUrlVideo = selectedWork.image_url?.toLowerCase().endsWith(".mp4") ?? false;

  const videoMedias = selectedWork.media_files?.filter(m => m.type === "video" && m.url) || [];
  const imageMedias = selectedWork.media_files?.filter(
    m => m.type === "image" && m.url && !m.url.toLowerCase().endsWith(".mp4")
  ) || [];

  // Filtrer les liens externes
  const youtubeMedia = selectedWork.media_files?.find(m => m.url?.includes("youtube"));
  const vimeoMedia = selectedWork.media_files?.find(m => m.url?.includes("vimeo"));
  const hasIframe = !!(youtubeMedia || vimeoMedia);

  const localVideos = videoMedias.filter(v => !v.url?.includes("youtube") && !v.url?.includes("vimeo"));

  const youtubeId = youtubeMedia?.url?.split("v=")[1];
  const vimeoId = vimeoMedia?.url?.split("/").pop();

  // =========================
  // Vidéo principale
  // =========================
  let mainVideo: string | null = null;

  if (!isImageUrlVideo && !hasIframe && localVideos.length > 0 && localVideos[0].url) {
    mainVideo = localVideos[0].url;
  }

  // =========================
  // Galerie
  // =========================
  const galleryMedias = [
    ...imageMedias,
    ...localVideos
  ].filter(m => m.url !== mainVideo && m.url !== selectedWork.image_url);

  // Helper pour BASEURL seulement si c'est local
  const resolveUrl = (url?: string) => url?.startsWith("http") ? url : `${BASEURL}${url}`;

  return (
    <div className="modalBackdrop" onClick={() => selectWork(null)}>
      <div className="modalContent" onClick={e => e.stopPropagation()}>

        {/* Bouton fermer */}
        <button className="closeButton" onClick={() => selectWork(null)}>
          &times;
        </button>

        {/* Titre */}
        <h2 style={{ color: COLORS.black }} className="modalTitle">
          {selectedWork.title}
        </h2>

        {/* Affiche principale */}
        {selectedWork.image_url && (
          isImageUrlVideo ? (
            <video
              src={resolveUrl(selectedWork.image_url)}
              className="modalMainVideo"
              autoPlay
              loop
              muted
              playsInline
              onClick={() => setZoomImage(resolveUrl(selectedWork.image_url))}
            />
          ) : (
            <img
              src={resolveUrl(selectedWork.image_url)}
              alt={selectedWork.title}
              className="modalMainImage"
              onClick={() => setZoomImage(resolveUrl(selectedWork.image_url))}
            />
          )
        )}

        {/* Vidéo principale si affiche = image et pas de lien externe */}
        {mainVideo && (
          <video
            src={resolveUrl(mainVideo)}
            controls
            className="modalVideo"
          />
        )}

        {/* Vimeo / YouTube */}
        {!mainVideo && vimeoId && (
          <iframe
            src={`https://player.vimeo.com/video/${vimeoId}`}
            title="Vimeo video"
            frameBorder="0"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            className="modalVideo"
          />
        )}
        {!mainVideo && !vimeoId && youtubeId && (
          <iframe
            src={`https://www.youtube.com/embed/${youtubeId}`}
            title="YouTube video"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="modalVideo"
          />
        )}

        {/* Description */}
        {selectedWork.description && (
          <p
            style={{
              color: COLORS.black,
              textAlign: "justify",
              textJustify: "inter-word",
              lineHeight: "1.6",
              whiteSpace: "pre-line",
              overflowWrap: "break-word",
            }}
            dangerouslySetInnerHTML={{ __html: sanitizeAndBold(selectedWork.description) }}
          />
        )}

        {/* Galerie */}
        {galleryMedias.length > 0 && (
          <div className="mediaList">
            {galleryMedias.map((m, i) => {
              const isVideo = m.type === "video" || m.url?.toLowerCase().endsWith(".mp4");
              return (
                <div key={i} className="mediaItem">
                  {isVideo ? (
                    <video
                      src={resolveUrl(m.url)}
                      className="image"
                      controls
                      onClick={() => setZoomImage(resolveUrl(m.url))}
                    />
                  ) : (
                    <img
                      src={resolveUrl(m.url)}
                      alt={`media-${i}`}
                      className="image"
                      onClick={() => setZoomImage(resolveUrl(m.url))}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}

      </div>

      {/* Zoom */}
      {zoomImage && (
        <div
          className="imageZoomOverlay"
          onClick={e => {
            e.stopPropagation();
            setZoomImage(null);
          }}
        >
          {zoomImage.toLowerCase().endsWith(".mp4") ? (
            <video
              src={zoomImage}
              className="imageZoom"
              controls
              onClick={e => e.stopPropagation()}
            />
          ) : (
            <img
              src={zoomImage}
              className="imageZoom"
              onClick={e => e.stopPropagation()}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default WorkModal;
